import React, { useState } from 'react';
import { User, Mail, Phone, Clock, Users, UserCheck, ExternalLink, AlertTriangle, Shield } from 'lucide-react';

const Team = () => {
  // Security escalation contacts
  const teamData = {
    general: [
      { 
        id: 1, 
        name: 'Muhammad Hamza', 
        shift: 'Morning',
        email: 'hamza.sakhi@abhi.co', 
        phone: '+92305-6848966',
        avatar: 'https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/male.png',
        role: 'General Queries & Help Contact'
      }
    ],
    tier1: [
      { 
        id: 2, 
        name: 'Shakeel Ahmad', 
        shift: 'Morning',
        email: 'shakeel@abhi.co', 
        phone: '+92300-0390522',
        avatar: 'https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/male.png',
        role: 'High Level Incident & Query - Tier Level 1'
      }
    ],
    tier2: [
      { 
        id: 3, 
        name: 'Ejaz Anwar', 
        shift: 'Morning',
        email: 'ejaz@abhi.co', 
        phone: '+92321-4633624',
        avatar: 'https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/male.png',
        role: 'High Level Incident & Query - Tier Level 2'
      }
    ],
    management: [
      { 
        id: 4, 
        name: 'Ali Ladhubhai', 
        shift: 'Morning',
        email: 'ali@abhi.co', 
        phone: '+92370-2244001',
        avatar: 'https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/male.png',
        role: 'Company Incident - Top Management Contact'
      }
    ]
  };

  return (
    <main className="container mx-auto px-3 sm:px-6 py-4 sm:py-8 relative">
      {/* HUD-Style Top Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-10 p-1 border border-teal-900/30 rounded-md bg-slate-800/30 backdrop-blur-sm">
        <div className="flex flex-wrap items-center gap-2 px-2 py-2 sm:py-0 sm:gap-4 sm:px-4">
          <div className="text-teal-400 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">ESCALATION:</span> ACTIVE
          </div>
          <div className="text-teal-400 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">TIERS:</span> 2
          </div>
          <div className="text-teal-400 font-mono text-xs sm:text-sm">
            <span className="text-slate-400">CONTACTS:</span> 4
          </div>
        </div>
        <div className="flex items-center px-2 py-2 sm:py-0">
          <div className="h-2 bg-gradient-to-r from-teal-800 via-teal-500 to-teal-800 animate-pulse mx-2 sm:mx-4" style={{ width: '60px', maxWidth: '25vw' }}></div>
          <div className="text-teal-400 font-mono text-xs px-2 sm:px-4">
            <span className="text-slate-500">TIME:</span> {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>

      {/* Page Header */}
      <div className="mb-8 sm:mb-12">
        <h1 className="text-2xl sm:text-4xl font-bold mb-1 relative">
          Security Incident <span className="text-teal-400">Escalation & Report</span>
          <div className="absolute -top-2 -right-2 w-2 h-2 bg-teal-500 rounded-full animate-ping"></div>
        </h1>
        <div className="flex flex-wrap items-center gap-2 text-teal-400 text-xs sm:text-base mb-4">
          <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Incident Response Contacts</span>
          <span className="w-1.5 h-1.5 rounded-full bg-teal-400 hidden sm:block"></span>
          <span className="hidden sm:inline">Security Escalation</span>
          <span className="w-1.5 h-1.5 rounded-full bg-teal-400 hidden sm:block"></span>
          <span className="hidden sm:inline">Emergency Response</span>
        </div>
        <p className="text-slate-300 text-xs sm:text-sm border-l-4 border-teal-800/50 pl-3 pr-4 py-2 bg-slate-800/30 rounded-r-md">
          Our Security Incident Escalation & Report system provides structured contact points for different types of security incidents 
          and queries. Contact the appropriate tier based on the severity and nature of your security concern to ensure proper handling 
          and timely response to threats and incidents.
        </p>
      </div>

      {/* General Queries Contact */}
      <div className="mb-8 sm:mb-12">
        <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6 flex items-center before:content-['//'] before:text-teal-500 before:mr-2 after:content-[''] after:h-px after:flex-grow after:bg-gradient-to-r after:from-teal-800/50 after:to-transparent after:ml-3">
          <User className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-teal-500" />
          General Queries & Help
        </h2>

        <div className="group relative overflow-hidden rounded-xl border border-slate-700 bg-gradient-to-br from-slate-800/90 to-slate-900/95 backdrop-blur-sm transition-all duration-300 hover:border-teal-600/50 hover:shadow-[0_0_40px_rgba(20,184,166,0.2)] hover:-translate-y-1">
          <div className="absolute inset-0 bg-gradient-to-br from-teal-900/10 to-cyan-900/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-teal-800 via-teal-500 to-teal-800 opacity-30 group-hover:opacity-80 transition-opacity"></div>
          
          <div className="p-4 sm:p-6 flex flex-col sm:flex-row gap-4 sm:gap-6 items-center relative">
            <div className="relative">
              <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-xl overflow-hidden border-4 border-teal-900/30 group-hover:border-teal-800/50 transition-colors">
                <img 
                  src={teamData.general[0].avatar} 
                  alt={teamData.general[0].name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-2 -right-2 bg-teal-800/80 px-2 py-1 rounded-lg border border-teal-600 text-xs text-white">
                <Clock className="h-3 w-3 inline-block mr-1" />
                Morning
              </div>
            </div>
            
            <div className="flex-1 text-center sm:text-left">
              <h3 className="text-xl sm:text-2xl font-bold mb-1">{teamData.general[0].name}</h3>
              <p className="text-teal-400 mb-3">{teamData.general[0].role}</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                <div className="bg-slate-800/40 p-2 rounded-lg border border-slate-700/80 flex items-center">
                  <Mail className="h-4 w-4 text-teal-400 mr-2" />
                  <span className="text-sm">{teamData.general[0].email}</span>
                </div>
                <div className="bg-slate-800/40 p-2 rounded-lg border border-slate-700/80 flex items-center">
                  <Phone className="h-4 w-4 text-teal-400 mr-2" />
                  <span className="text-sm">{teamData.general[0].phone}</span>
                </div>
              </div>
              
              <p className="text-sm text-slate-400 bg-slate-800/30 p-3 rounded-lg border border-slate-700/60">
                {teamData.general[0].name} handles general queries and provides help for basic security questions and concerns. 
                Contact for initial assistance, general information, and non-critical security guidance.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Escalation Contacts Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
        {/* Tier 1 Contact */}
        {teamData.tier1.map(member => (
          <div 
            key={member.id}
            className="group relative overflow-hidden rounded-lg border border-yellow-700 bg-gradient-to-br from-slate-800/90 to-slate-900/95 backdrop-blur-sm transition-all duration-300 hover:border-yellow-600/50 hover:shadow-[0_0_30px_rgba(234,179,8,0.15)] hover:-translate-y-1"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-800 via-yellow-500 to-yellow-800 opacity-30 group-hover:opacity-80 transition-opacity"></div>
            
            <div className="p-4 flex gap-3 items-center">
              <div className="relative">
                <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-yellow-900/30 group-hover:border-yellow-800/50 transition-colors">
                  <img 
                    src={member.avatar} 
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 bg-yellow-800/80 px-1.5 py-0.5 rounded-md border border-yellow-600 text-[10px] text-white">
                  {member.shift}
                </div>
              </div>
              
              <div className="flex-1">
                <h3 className="font-bold text-lg">{member.name}</h3>
                <p className="text-yellow-400 text-sm">{member.role}</p>
              </div>
            </div>
            
            <div className="px-4 pb-4 pt-0">
              <div className="grid grid-cols-1 gap-2">
                <div className="bg-slate-800/40 p-2 rounded border border-slate-700/80 flex items-center text-sm">
                  <Mail className="h-3.5 w-3.5 text-yellow-400 mr-2 flex-shrink-0" />
                  <span className="truncate">{member.email}</span>
                </div>
                <div className="bg-slate-800/40 p-2 rounded border border-slate-700/80 flex items-center text-sm">
                  <Phone className="h-3.5 w-3.5 text-yellow-400 mr-2 flex-shrink-0" />
                  <span>{member.phone}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {/* Tier 2 Contact */}
        {teamData.tier2.map(member => (
          <div 
            key={member.id}
            className="group relative overflow-hidden rounded-lg border border-orange-700 bg-gradient-to-br from-slate-800/90 to-slate-900/95 backdrop-blur-sm transition-all duration-300 hover:border-orange-600/50 hover:shadow-[0_0_30px_rgba(249,115,22,0.15)] hover:-translate-y-1"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-800 via-orange-500 to-orange-800 opacity-30 group-hover:opacity-80 transition-opacity"></div>
            
            <div className="p-4 flex gap-3 items-center">
              <div className="relative">
                <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-orange-900/30 group-hover:border-orange-800/50 transition-colors">
                  <img 
                    src={member.avatar} 
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 bg-orange-800/80 px-1.5 py-0.5 rounded-md border border-orange-600 text-[10px] text-white">
                  {member.shift}
                </div>
              </div>
              
              <div className="flex-1">
                <h3 className="font-bold text-lg">{member.name}</h3>
                <p className="text-orange-400 text-sm">{member.role}</p>
              </div>
            </div>
            
            <div className="px-4 pb-4 pt-0">
              <div className="grid grid-cols-1 gap-2">
                <div className="bg-slate-800/40 p-2 rounded border border-slate-700/80 flex items-center text-sm">
                  <Mail className="h-3.5 w-3.5 text-orange-400 mr-2 flex-shrink-0" />
                  <span className="truncate">{member.email}</span>
                </div>
                <div className="bg-slate-800/40 p-2 rounded border border-slate-700/80 flex items-center text-sm">
                  <Phone className="h-3.5 w-3.5 text-orange-400 mr-2 flex-shrink-0" />
                  <span>{member.phone}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {/* Top Management Contact */}
        {teamData.management.map(member => (
          <div 
            key={member.id}
            className="group relative overflow-hidden rounded-lg border border-red-700 bg-gradient-to-br from-slate-800/90 to-slate-900/95 backdrop-blur-sm transition-all duration-300 hover:border-red-600/50 hover:shadow-[0_0_30px_rgba(239,68,68,0.15)] hover:-translate-y-1"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-800 via-red-500 to-red-800 opacity-30 group-hover:opacity-80 transition-opacity"></div>
            
            <div className="p-4 flex gap-3 items-center">
              <div className="relative">
                <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-red-900/30 group-hover:border-red-800/50 transition-colors">
                  <img 
                    src={member.avatar} 
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 bg-red-800/80 px-1.5 py-0.5 rounded-md border border-red-600 text-[10px] text-white">
                  {member.shift}
                </div>
              </div>
              
              <div className="flex-1">
                <h3 className="font-bold text-lg">{member.name}</h3>
                <p className="text-red-400 text-sm">{member.role}</p>
              </div>
            </div>
            
            <div className="px-4 pb-4 pt-0">
              <div className="grid grid-cols-1 gap-2">
                <div className="bg-slate-800/40 p-2 rounded border border-slate-700/80 flex items-center text-sm">
                  <Mail className="h-3.5 w-3.5 text-red-400 mr-2 flex-shrink-0" />
                  <span className="truncate">{member.email}</span>
                </div>
                <div className="bg-slate-800/40 p-2 rounded border border-slate-700/80 flex items-center text-sm">
                  <Phone className="h-3.5 w-3.5 text-red-400 mr-2 flex-shrink-0" />
                  <span>{member.phone}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Security Escalation Information */}
      <div className="bg-slate-800/70 border border-slate-700 rounded-lg p-4 sm:p-6 mb-8">
        <h2 className="text-lg sm:text-xl font-bold mb-3 flex items-center">
          <img src="https://pub-4669a21320a8443b99f81a79e9cea5e8.r2.dev/Copy%20of%20App-logo-animate-revised-white.gif" alt="Abhi" className="h-5 w-5 mr-2" />
          Security Escalation Guidelines
        </h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div className="bg-slate-800/40 p-3 rounded-lg border border-slate-700">
            <h3 className="font-bold text-blue-400 flex items-center mb-2">
              <User className="h-4 w-4 mr-2" />
              General Queries & Help
            </h3>
            <p className="text-sm text-slate-300">
              Contact Muhammad Hamza for general security questions, basic assistance, 
              initial guidance, and non-critical security concerns that don't require immediate escalation.
            </p>
          </div>
          
          <div className="bg-slate-800/40 p-3 rounded-lg border border-slate-700">
            <h3 className="font-bold text-yellow-400 flex items-center mb-2">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Tier Level 1 - Critical Security Operations
            </h3>
            <p className="text-sm text-slate-300">
              Contact Shakeel Ahmad for critical security operations, advanced threat analysis, 
              security incident response, vulnerability assessments, breach investigations, 
              and complex cybersecurity challenges requiring specialized technical expertise and immediate action.
            </p>
          </div>
          
          <div className="bg-slate-800/40 p-3 rounded-lg border border-slate-700">
            <h3 className="font-bold text-orange-400 flex items-center mb-2">
              <Shield className="h-4 w-4 mr-2" />
              Tier Level 2 - Advanced Security Architecture
            </h3>
            <p className="text-sm text-slate-300">
              Contact Ejaz Anwar for advanced security architecture, strategic security planning, 
              complex technology integration, and cross-functional coordination.
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-4 mb-4">
          <div className="bg-slate-800/40 p-3 rounded-lg border border-red-700/50">
            <h3 className="font-bold text-red-400 flex items-center mb-2">
              <ExternalLink className="h-4 w-4 mr-2" />
              Top Management - Company Incidents
            </h3>
            <p className="text-sm text-slate-300">
              Contact Ali Ladhubhai for company-wide security incidents, executive-level reporting, 
              major data breaches, regulatory compliance issues, and incidents requiring top management involvement.
            </p>
          </div>
        </div>
        
        <div className="bg-slate-800/50 p-4 rounded-lg border border-teal-900/30 text-sm">
          <p className="mb-2">
            Our Security Incident Escalation system is designed to provide appropriate response levels for different types of security concerns. 
            Please contact the appropriate tier based on the severity and nature of your incident to ensure proper handling and response time.
          </p>
          <p>
            All contacts are available during morning shift hours. For emergency situations outside of regular hours, 
            please use the provided phone numbers for immediate assistance and incident reporting.
          </p>
        </div>
      </div>
    </main>
  );
};

export default Team;
